/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `validation` for the Caintra Connect frontend.
 * @module utils/validation
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
// Validation rules for different field types
export const VALIDATION_RULES = {
  asunto: {
    required: false,
    maxLength: 150,
    pattern: /^[a-zA-ZÀ-ÿ0-9\s,\.]+$/,
    patternDescription: "ALPHANUMERIC_WITH_COMMAS_AND_DOTS",
  },
  mensaje: {
    required: false,
    maxLength: 300,
    pattern: /^[a-zA-ZÀ-ÿ0-9\s,\.]+$/,
    patternDescription: "ALPHANUMERIC_WITH_COMMAS_AND_DOTS",
  },
  nombreComercial: {
    required: true,
    maxLength: 100,
    pattern: /^[a-zA-ZÀ-ÿ0-9\s,\.]+$/,
    patternDescription: "ALPHANUMERIC_ONLY",
  },

  RFC: {
    required: true,
    pattern: /^[A-Z&Ñ]{3,4}[0-9]{6}[A-Z0-9]{3}$/,
    patternDescription: "INVALID_RFC",
  },
  companyRFC: {
    required: true,
    // Company RFC (Persona Moral): 12 characters - 3 letters, 6 digits, 3 alphanumeric
    pattern: /^[A-ZÑ&]{3}\d{6}[A-Z0-9]{3}$/,
    patternDescription: "INVALID_RFC",
    customValidation: (value) => {
      // Empty value is handled by required check in validateField
      if (!value || value.trim() === "") {
        return { isValid: true };
      }

      const cleanRFC = value.trim().toUpperCase();

      // Check pattern match (must be exactly 12 characters)
      if (
        cleanRFC.length !== 12 ||
        !/^[A-ZÑ&]{3}\d{6}[A-Z0-9]{3}$/.test(cleanRFC)
      ) {
        return { isValid: false, error: "INVALID_RFC" };
      }

      // Validate date part (characters 3-8)
      const dateString = cleanRFC.substring(3, 9);
      const month = parseInt(dateString.substring(2, 4), 10);
      const day = parseInt(dateString.substring(4, 6), 10);

      // Validate date components
      if (isNaN(month) || month < 1 || month > 12) {
        return { isValid: false, error: "INVALID_RFC" };
      }
      if (isNaN(day) || day < 1 || day > 31) {
        return { isValid: false, error: "INVALID_RFC" };
      }

      return { isValid: true };
    },
  },
  taxId: {
    required: true,
    pattern: /^[A-Z&Ñ]{3,4}[0-9]{6}[A-Z0-9]{3}$/,
    patternDescription: "INVALID_RFC",
  },
  email: {
    required: false,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    patternDescription: "INVALID_EMAIL",
  },
  phone: {
    required: false,
    pattern: /^[\d\s\-\+\(\)]+$/,
    patternDescription: "INVALID_PHONE",
  },
  website: {
    required: false,
    pattern: /^https?:\/\/.+/,
    patternDescription: "INVALID_WEBSITE",
  },
  description: {
    required: false,
    maxLength: 500,
    pattern: /^[a-zA-ZÀ-ÿ0-9\s,\.]+$/,
    patternDescription: "ALPHANUMERIC_ONLY",
  },
  keywords: {
    required: false,
    maxLength: 255,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    patternDescription: "LETTERS_ONLY",
  },
  domicilio1Calle: {
    required: false,
    maxLength: 100,
    pattern: /^[a-zA-ZÀ-ÿ0-9\s\.,]+$/,
    patternDescription: "ALPHANUMERIC_ONLY",
  },
  domicilio1NumeroExterior: {
    required: false,
    maxLength: 10,
    pattern: /^\d+$/,
    patternDescription: "NUMBERS_ONLY",
  },
  domicilio1NumeroInterior: {
    required: false,
    maxLength: 10,
    pattern: /^[a-zA-Z0-9-\.]+$/,
    patternDescription: "ALPHANUMERIC_WITH_HYPHENS_AND_DOTS",
  },
  domicilio1EntreCalle1: {
    required: false,
    maxLength: 100,
    pattern: /^[a-zA-ZÀ-ÿ0-9\s\.,]+$/,
    patternDescription: "ALPHANUMERIC_ONLY",
  },
  domicilio1EntreCalle2: {
    required: false,
    maxLength: 100,
    pattern: /^[a-zA-ZÀ-ÿ0-9\s\.,]+$/,
    patternDescription: "ALPHANUMERIC_ONLY",
  },
  // Second address validation rules
  domicilio2Calle: {
    required: true,
    maxLength: 100,
    pattern: /^[a-zA-ZÀ-ÿ0-9\s\.,]+$/,
    patternDescription: "ALPHANUMERIC_ONLY",
  },
  domicilio2NumeroExterior: {
    required: false,
    maxLength: 10,
    pattern: /^[a-zA-ZÀ-ÿ0-9\s\.\-]+$/,
    patternDescription: "ALPHANUMERIC_ONLY",
  },
  domicilio2NumeroInterior: {
    required: false,
    maxLength: 10,
    pattern: /^[a-zA-Z0-9-\.]+$/,
    patternDescription: "ALPHANUMERIC_WITH_HYPHENS_AND_DOTS",
  },
  domicilio2EntreCalle1: {
    required: false,
    maxLength: 100,
    pattern: /^[a-zA-ZÀ-ÿ0-9\s\.,]+$/,
    patternDescription: "ALPHANUMERIC_ONLY",
  },
  domicilio2EntreCalle2: {
    required: false,
    maxLength: 100,
    pattern: /^[a-zA-ZÀ-ÿ0-9\s\.,]+$/,
    patternDescription: "ALPHANUMERIC_ONLY",
  },
  sucursales: {
    required: false,
    maxLength: 300,
    pattern: /^[a-zA-ZÀ-ÿ0-9\s,]+$/,
    patternDescription: "ALPHANUMERIC_WITH_COMMAS",
  },
  productoPrincipal: {
    required: false,
    maxLength: 200,
    pattern: /^[a-zA-ZÀ-ÿ0-9\s]+$/,
    patternDescription: "ALPHANUMERIC_WITH_SPACES",
  },
  materiaPrima: {
    required: false,
    maxLength: 200,
    pattern: /^[a-zA-ZÀ-ÿ0-9\s]+$/,
    patternDescription: "ALPHANUMERIC_WITH_SPACES",
  },
  certificacionesDeCalidad: {
    required: false,
    maxLength: 200,
    pattern: /^[a-zA-ZÀ-ÿ0-9\s-,]+$/,
    patternDescription: "ALPHANUMERIC_WITH_COMMAS_AND_HYPHENS",
  },
  maquinariaYEquipo: {
    required: false,
    maxLength: 300,
    pattern: /^[a-zA-ZÀ-ÿ0-9\s,]+$/,
    patternDescription: "ALPHANUMERIC_WITH_COMMAS",
  },
  procesosYEspecialidades: {
    required: false,
    maxLength: 300,
    pattern: /^[a-zA-ZÀ-ÿ0-9\s-]+$/,
    patternDescription: "ALPHANUMERIC_WITH_HYPHENS",
  },
  clientes: {
    required: false,
    maxLength: 300,
    pattern: /^[a-zA-ZÀ-ÿ0-9\s-]+$/,
    patternDescription: "ALPHANUMERIC_WITH_HYPHENS",
  },
  linkedIn: {
    required: false,
    maxLength: 2083,
    pattern:
      /^https:\/\/[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}(\/.*)?$/,
    patternDescription: "INVALID_WEBSITE",
  },
  facebook: {
    required: false,
    maxLength: 2083,
    pattern:
      /^https:\/\/[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}(\/.*)?$/,
    patternDescription: "INVALID_WEBSITE",
  },
  twitter: {
    required: false,
    maxLength: 2083,
    pattern:
      /^https:\/\/[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}(\/.*)?$/,
    patternDescription: "INVALID_WEBSITE",
  },
  youtube: {
    required: false,
    maxLength: 2083,
    pattern:
      /^https:\/\/[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}(\/.*)?$/,
    patternDescription: "INVALID_WEBSITE",
  },
  instagram: {
    required: false,
    maxLength: 2083,
    pattern:
      /^https:\/\/[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}(\/.*)?$/,
    patternDescription: "INVALID_WEBSITE",
  },
  capacidadInstalada: {
    required: false,
    maxLength: 300,
    pattern: /^[a-zA-ZÀ-ÿ0-9\s]+$/,
    patternDescription: "ALPHANUMERIC_WITH_SPACES",
  },
  clientesPrincipalesYReferenciasComerciales: {
    required: false,
    maxLength: 300,
    pattern: /^[a-zA-ZÀ-ÿ0-9\s]+$/,
    patternDescription: "ALPHANUMERIC_WITH_SPACES",
  },
  requerimientoTitulo: {
    required: false,
    maxLength: 100,
    pattern: /^[a-zA-ZÀ-ÿ0-9\s]+$/,
    patternDescription: "ALPHANUMERIC_WITH_SPACES",
  },
  requerimientoDescripcion: {
    required: false,
    maxLength: 300,
    pattern: /^[a-zA-ZÀ-ÿ0-9\s,\.]+$/,
    patternDescription: "ALPHANUMERIC_WITH_COMMAS_AND_DOTS",
  },
  requerimientoUbicacion: {
    required: false,
    maxLength: 100,
    pattern: /^[a-zA-ZÀ-ÿ0-9\s,-]+$/,
    patternDescription: "ALPHANUMERIC_WITH_COMMAS_AND_HYPHENS",
  },
  certificacionExperiencia: {
    required: false,
    maxLength: 2,
    pattern: /^\d+$/,
    patternDescription: "NUMBERS_ONLY",
  },
  requerimientoExperienciaMinima: {
    required: false,
    pattern: /^-?\d{0,2}(\.\d{0,1})?$/,
    patternDescription: "INVALID_EXPERIENCE_FORMAT",
    customValidation: (value) => {
      if (!value || value === "") {
        return { isValid: true };
      }

      if (
        value === "-0" ||
        value === "-0." ||
        value === "-0.0" ||
        value === "-00"
      ) {
        return { isValid: false, error: "EXPERIENCE_CANNOT_BE_NEGATIVE" };
      }

      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        return { isValid: false, error: "INVALID_EXPERIENCE_FORMAT" };
      }
      if (numValue > 99.9) {
        return { isValid: false, error: "EXPERIENCE_TOO_HIGH" };
      }
      if (numValue < 0) {
        return { isValid: false, error: "EXPERIENCE_CANNOT_BE_NEGATIVE" };
      }
      return { isValid: true };
    },
  },
  requerimientoVolumen: {
    required: false,
    maxLength: 100,
    pattern: /^\s*[0-9]+(\.[0-9]+)?\s*([a-zA-ZÀ-ÿ\/]*)?\s*$/,
    patternDescription: "NUMERIC_WITH_OPTIONAL_UNIT_AND_SLASH",
    customValidation: (value) => {
      if (!value || value.trim() === "") {
        return { isValid: true };
      }

      const trimmed = value.trim();

      const fullMatch = trimmed.match(
        /^[0-9]+(\.[0-9]+)?([ ]?[a-zA-ZÀ-ÿ\/]*)?$/,
      );
      if (!fullMatch) {
        return { isValid: false, error: "INVALID_FORMAT_OR_SYMBOLS" };
      }

      const numMatch = trimmed.match(/^([0-9]+(\.[0-9]+)?)/);
      if (numMatch) {
        const numValue = parseFloat(numMatch[1]);
        if (isNaN(numValue)) {
          return { isValid: false, error: "INVALID_NUMBER_FORMAT" };
        }
        if (numValue < 0) {
          return { isValid: false, error: "NEGATIVE_NUMBERS_NOT_ALLOWED" };
        }
      } else {
        return { isValid: false, error: "INVALID_NUMBER_FORMAT" };
      }

      return { isValid: true };
    },
  },
  tiempoDeEntrega: {
    required: false,
    maxLength: 100,
    pattern: /^[0-9]{1,3}([ ]?[a-zA-ZÀ-ÿ\s]*)?$/,
    patternDescription: "DELIVERY_TIME_FORMAT",
    customValidation: (value) => {
      if (!value || value === "") {
        return { isValid: true };
      }

      // Check if the value matches the expected format: number + optional text
      const trimmedValue = value.trim();

      // Must start with 1-3 digits
      if (!/^\d{1,3}/.test(trimmedValue)) {
        return { isValid: false, error: "DELIVERY_TIME_FORMAT" };
      }

      // Check for negative numbers
      if (trimmedValue.includes("-")) {
        return { isValid: false, error: "NEGATIVE_NUMBERS_NOT_ALLOWED" };
      }

      // Check for decimal numbers
      if (trimmedValue.includes(".")) {
        return { isValid: false, error: "DECIMAL_NUMBERS_NOT_ALLOWED" };
      }

      // Extract the numeric part and check if it's more than 3 digits
      const numericMatch = trimmedValue.match(/^\d+/);
      if (numericMatch && numericMatch[0].length > 3) {
        return { isValid: false, error: "DELIVERY_TIME_FORMAT" };
      }

      return { isValid: true };
    },
  },
  interesesComerciales: {
    required: false,
    maxLength: 255,
    pattern: /^[a-zA-ZÀ-ÿ\s,\.]+$/,
    patternDescription: "LETTERS_WITH_COMMAS_AND_DOTS",
  },
  expectativas: {
    required: false,
    maxLength: 300,
    pattern: /^[a-zA-ZÀ-ÿ\s,\.]+$/,
    patternDescription: "LETTERS_WITH_COMMAS_AND_DOTS",
  },
  propuestaDescripcion: {
    required: false,
    maxLength: 300,
    pattern: /^[a-zA-ZÀ-ÿ0-9\s,\.]+$/,
    patternDescription: "ALPHANUMERIC_WITH_COMMAS_AND_DOTS",
  },
  propuestaCosto: {
    required: false,
    pattern: /^\d{1,6}(\.\d{1,2})?$/,
    patternDescription: "COST_NUMBER_TOO_LARGE",
    customValidation: (value) => {
      if (!value || value === "") {
        return { isValid: true };
      }
      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        return { isValid: false, error: "INVALID_NUMBER_FORMAT" };
      }
      if (numValue > 999999.99) {
        return { isValid: false, error: "COST_NUMBER_TOO_LARGE" };
      }
      if (numValue < 0) {
        return { isValid: false, error: "NUMBER_TOO_SMALL" };
      }
      return { isValid: true };
    },
  },
  telefonoDeOficina: {
    required: false,
    pattern: /^[\d\s]+$/,
    patternDescription: "PHONE_MUST_BE_10_DIGITS",
    customValidation: (value) => {
      if (!value || value.trim() === "") {
        return { isValid: true };
      }

      // Remove all spaces and check if it's exactly 10 digits
      const digitsOnly = value.replace(/\s/g, "");
      if (!/^\d{10}$/.test(digitsOnly)) {
        return { isValid: false, error: "PHONE_MUST_BE_10_DIGITS" };
      }

      return { isValid: true };
    },
  },
  extensionTelefono: {
    required: false,
    pattern: /^\d{1,5}$/,
    maxLength: 5,
    patternDescription: "PHONE_EXTENSION_MAX_5_DIGITS",
  },
  telefonoSecundario: {
    required: false,
    pattern: /^[\d\s]+$/,
    patternDescription: "PHONE_MUST_BE_10_DIGITS",
    customValidation: (value) => {
      if (!value || value.trim() === "") {
        return { isValid: true };
      }

      // Remove all spaces and check if it's exactly 10 digits
      const digitsOnly = value.replace(/\s/g, "");
      if (!/^\d{10}$/.test(digitsOnly)) {
        return { isValid: false, error: "PHONE_MUST_BE_10_DIGITS" };
      }

      return { isValid: true };
    },
  },
  correoElectronicoPrincipal: {
    required: false,
    pattern:
      /^(?!.*\.\.)[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z]{2,})+$/,
    maxLength: 100,
    patternDescription: "INVALID_EMAIL",
  },
  paginaInternet: {
    required: false,
    pattern:
      /^https:\/\/[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}(\/.*)?$/,
    maxLength: 2083,
    patternDescription: "INVALID_WEBSITE",
  },
  contacto1Nombre: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto1SegundoNombre: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto1ApellidoPaterno: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto1ApellidoMaterno: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto1Telefono: {
    required: false,
    pattern: /^[\d\s]+$/,
    patternDescription: "PHONE_MUST_BE_10_DIGITS",
    customValidation: (value) => {
      if (!value || value.trim() === "") {
        return { isValid: true };
      }

      // Remove all spaces and check if it's exactly 10 digits
      const digitsOnly = value.replace(/\s/g, "");
      if (!/^\d{10}$/.test(digitsOnly)) {
        return { isValid: false, error: "PHONE_MUST_BE_10_DIGITS" };
      }

      return { isValid: true };
    },
  },
  contacto1Celular: {
    required: false,
    pattern: /^[\d\s]+$/,
    patternDescription: "PHONE_MUST_BE_10_DIGITS",
    customValidation: (value) => {
      if (!value || value.trim() === "") {
        return { isValid: true };
      }

      // Remove all spaces and check if it's exactly 10 digits
      const digitsOnly = value.replace(/\s/g, "");
      if (!/^\d{10}$/.test(digitsOnly)) {
        return { isValid: false, error: "PHONE_MUST_BE_10_DIGITS" };
      }

      return { isValid: true };
    },
  },
  contacto1Extension: {
    required: false,
    pattern: /^\d{1,5}$/,
    maxLength: 5,
    patternDescription: "PHONE_EXTENSION_MAX_5_DIGITS",
  },
  contacto1CorreoElectronico: {
    required: false,
    pattern:
      /^(?!.*\.\.)[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z]{2,})+$/,
    maxLength: 100,
    patternDescription: "INVALID_EMAIL",
  },
  contacto1FechaNacimiento: {
    required: false,
    customValidation: (value) => {
      if (!value) {
        return { isValid: true };
      } // Optional field

      const birthDate = new Date(value);
      const today = new Date();
      const minAge = 18;
      const maxAge = 100;

      // Check if it's a valid date
      if (isNaN(birthDate.getTime())) {
        return { isValid: false, error: "INVALID_DATE" };
      }

      // Check if it's in the future
      if (birthDate > today) {
        return { isValid: false, error: "FUTURE_DATE_NOT_ALLOWED" };
      }

      // Check minimum age (18 years old)
      const minDate = new Date();
      minDate.setFullYear(today.getFullYear() - minAge);
      if (birthDate > minDate) {
        return { isValid: false, error: "MINIMUM_AGE_REQUIRED" };
      }

      // Check maximum age (100 years old)
      const maxDate = new Date();
      maxDate.setFullYear(today.getFullYear() - maxAge);
      if (birthDate < maxDate) {
        return { isValid: false, error: "MAXIMUM_AGE_EXCEEDED" };
      }

      return { isValid: true };
    },
  },
  // Additional contacts validation rules (contacto2, contacto3, contacto4, contacto5)
  contacto2Nombre: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto2SegundoNombre: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto2ApellidoPaterno: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto2ApellidoMaterno: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto2Telefono: {
    required: false,
    pattern: /^[\d\s]+$/,
    patternDescription: "PHONE_MUST_BE_10_DIGITS",
    customValidation: (value) => {
      if (!value || value.trim() === "") {
        return { isValid: true };
      }

      // Remove all spaces and check if it's exactly 10 digits
      const digitsOnly = value.replace(/\s/g, "");
      if (!/^\d{10}$/.test(digitsOnly)) {
        return { isValid: false, error: "PHONE_MUST_BE_10_DIGITS" };
      }

      return { isValid: true };
    },
  },
  contacto2Celular: {
    required: false,
    pattern: /^[\d\s]+$/,
    patternDescription: "PHONE_MUST_BE_10_DIGITS",
    customValidation: (value) => {
      if (!value || value.trim() === "") {
        return { isValid: true };
      }

      // Remove all spaces and check if it's exactly 10 digits
      const digitsOnly = value.replace(/\s/g, "");
      if (!/^\d{10}$/.test(digitsOnly)) {
        return { isValid: false, error: "PHONE_MUST_BE_10_DIGITS" };
      }

      return { isValid: true };
    },
  },
  contacto2Extension: {
    required: false,
    pattern: /^\d{1,5}$/,
    maxLength: 5,
    patternDescription: "PHONE_EXTENSION_MAX_5_DIGITS",
  },
  contacto2CorreoElectronico: {
    required: false,
    pattern:
      /^(?!.*\.\.)[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z]{2,})+$/,
    maxLength: 100,
    patternDescription: "INVALID_EMAIL",
  },
  contacto2FechaNacimiento: {
    required: false,
    customValidation: (value) => {
      if (!value) {
        return { isValid: true };
      } // Optional field

      const birthDate = new Date(value);
      const today = new Date();
      const minAge = 18;
      const maxAge = 100;

      // Check if it's a valid date
      if (isNaN(birthDate.getTime())) {
        return { isValid: false, error: "INVALID_DATE" };
      }

      // Check if it's in the future
      if (birthDate > today) {
        return { isValid: false, error: "FUTURE_DATE_NOT_ALLOWED" };
      }

      // Check minimum age (18 years old)
      const minDate = new Date();
      minDate.setFullYear(today.getFullYear() - minAge);
      if (birthDate > minDate) {
        return { isValid: false, error: "MINIMUM_AGE_REQUIRED" };
      }

      // Check maximum age (100 years old)
      const maxDate = new Date();
      maxDate.setFullYear(today.getFullYear() - maxAge);
      if (birthDate < maxDate) {
        return { isValid: false, error: "MAXIMUM_AGE_EXCEEDED" };
      }

      return { isValid: true };
    },
  },
  contacto3Nombre: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto3SegundoNombre: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto3ApellidoPaterno: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto3ApellidoMaterno: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto3Telefono: {
    required: false,
    pattern: /^[\d\s]+$/,
    patternDescription: "PHONE_MUST_BE_10_DIGITS",
    customValidation: (value) => {
      if (!value || value.trim() === "") {
        return { isValid: true };
      }

      // Remove all spaces and check if it's exactly 10 digits
      const digitsOnly = value.replace(/\s/g, "");
      if (!/^\d{10}$/.test(digitsOnly)) {
        return { isValid: false, error: "PHONE_MUST_BE_10_DIGITS" };
      }

      return { isValid: true };
    },
  },
  contacto3Celular: {
    required: false,
    pattern: /^[\d\s]+$/,
    patternDescription: "PHONE_MUST_BE_10_DIGITS",
    customValidation: (value) => {
      if (!value || value.trim() === "") {
        return { isValid: true };
      }

      // Remove all spaces and check if it's exactly 10 digits
      const digitsOnly = value.replace(/\s/g, "");
      if (!/^\d{10}$/.test(digitsOnly)) {
        return { isValid: false, error: "PHONE_MUST_BE_10_DIGITS" };
      }

      return { isValid: true };
    },
  },
  contacto3Extension: {
    required: false,
    pattern: /^\d{1,5}$/,
    maxLength: 5,
    patternDescription: "PHONE_EXTENSION_MAX_5_DIGITS",
  },
  contacto3CorreoElectronico: {
    required: false,
    pattern:
      /^(?!.*\.\.)[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z]{2,})+$/,
    maxLength: 100,
    patternDescription: "INVALID_EMAIL",
  },
  contacto3FechaNacimiento: {
    required: false,
    customValidation: (value) => {
      if (!value) {
        return { isValid: true };
      } // Optional field

      const birthDate = new Date(value);
      const today = new Date();
      const minAge = 18;
      const maxAge = 100;

      // Check if it's a valid date
      if (isNaN(birthDate.getTime())) {
        return { isValid: false, error: "INVALID_DATE" };
      }

      // Check if it's in the future
      if (birthDate > today) {
        return { isValid: false, error: "FUTURE_DATE_NOT_ALLOWED" };
      }

      // Check minimum age (18 years old)
      const minDate = new Date();
      minDate.setFullYear(today.getFullYear() - minAge);
      if (birthDate > minDate) {
        return { isValid: false, error: "MINIMUM_AGE_REQUIRED" };
      }

      // Check maximum age (100 years old)
      const maxDate = new Date();
      maxDate.setFullYear(today.getFullYear() - maxAge);
      if (birthDate < maxDate) {
        return { isValid: false, error: "MAXIMUM_AGE_EXCEEDED" };
      }

      return { isValid: true };
    },
  },
  contacto4Nombre: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto4SegundoNombre: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto4ApellidoPaterno: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto4ApellidoMaterno: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto4Telefono: {
    required: false,
    pattern: /^[\d\s]+$/,
    patternDescription: "PHONE_MUST_BE_10_DIGITS",
    customValidation: (value) => {
      if (!value || value.trim() === "") {
        return { isValid: true };
      }

      // Remove all spaces and check if it's exactly 10 digits
      const digitsOnly = value.replace(/\s/g, "");
      if (!/^\d{10}$/.test(digitsOnly)) {
        return { isValid: false, error: "PHONE_MUST_BE_10_DIGITS" };
      }

      return { isValid: true };
    },
  },
  contacto4Celular: {
    required: false,
    pattern: /^[\d\s]+$/,
    patternDescription: "PHONE_MUST_BE_10_DIGITS",
    customValidation: (value) => {
      if (!value || value.trim() === "") {
        return { isValid: true };
      }

      // Remove all spaces and check if it's exactly 10 digits
      const digitsOnly = value.replace(/\s/g, "");
      if (!/^\d{10}$/.test(digitsOnly)) {
        return { isValid: false, error: "PHONE_MUST_BE_10_DIGITS" };
      }

      return { isValid: true };
    },
  },
  contacto4Extension: {
    required: false,
    pattern: /^\d{1,5}$/,
    maxLength: 5,
    patternDescription: "PHONE_EXTENSION_MAX_5_DIGITS",
  },
  contacto4CorreoElectronico: {
    required: false,
    pattern:
      /^(?!.*\.\.)[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z]{2,})+$/,
    maxLength: 100,
    patternDescription: "INVALID_EMAIL",
  },
  contacto4FechaNacimiento: {
    required: false,
    customValidation: (value) => {
      if (!value) {
        return { isValid: true };
      } // Optional field

      const birthDate = new Date(value);
      const today = new Date();
      const minAge = 18;
      const maxAge = 100;

      // Check if it's a valid date
      if (isNaN(birthDate.getTime())) {
        return { isValid: false, error: "INVALID_DATE" };
      }

      // Check if it's in the future
      if (birthDate > today) {
        return { isValid: false, error: "FUTURE_DATE_NOT_ALLOWED" };
      }

      // Check minimum age (18 years old)
      const minDate = new Date();
      minDate.setFullYear(today.getFullYear() - minAge);
      if (birthDate > minDate) {
        return { isValid: false, error: "MINIMUM_AGE_REQUIRED" };
      }

      // Check maximum age (100 years old)
      const maxDate = new Date();
      maxDate.setFullYear(today.getFullYear() - maxAge);
      if (birthDate < maxDate) {
        return { isValid: false, error: "MAXIMUM_AGE_EXCEEDED" };
      }

      return { isValid: true };
    },
  },
  contacto5Nombre: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto5SegundoNombre: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto5ApellidoPaterno: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto5ApellidoMaterno: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto5Telefono: {
    required: false,
    pattern: /^[\d\s]+$/,
    patternDescription: "PHONE_MUST_BE_10_DIGITS",
    customValidation: (value) => {
      if (!value || value.trim() === "") {
        return { isValid: true };
      }

      // Remove all spaces and check if it's exactly 10 digits
      const digitsOnly = value.replace(/\s/g, "");
      if (!/^\d{10}$/.test(digitsOnly)) {
        return { isValid: false, error: "PHONE_MUST_BE_10_DIGITS" };
      }

      return { isValid: true };
    },
  },
  contacto5Celular: {
    required: false,
    pattern: /^[\d\s]+$/,
    patternDescription: "PHONE_MUST_BE_10_DIGITS",
    customValidation: (value) => {
      if (!value || value.trim() === "") {
        return { isValid: true };
      }

      // Remove all spaces and check if it's exactly 10 digits
      const digitsOnly = value.replace(/\s/g, "");
      if (!/^\d{10}$/.test(digitsOnly)) {
        return { isValid: false, error: "PHONE_MUST_BE_10_DIGITS" };
      }

      return { isValid: true };
    },
  },
  contacto5Extension: {
    required: false,
    pattern: /^\d{1,5}$/,
    maxLength: 5,
    patternDescription: "PHONE_EXTENSION_MAX_5_DIGITS",
  },
  contacto5CorreoElectronico: {
    required: false,
    pattern:
      /^(?!.*\.\.)[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z]{2,})+$/,
    maxLength: 100,
    patternDescription: "INVALID_EMAIL",
  },
  contacto5FechaNacimiento: {
    required: false,
    customValidation: (value) => {
      if (!value) {
        return { isValid: true };
      } // Optional field

      const birthDate = new Date(value);
      const today = new Date();
      const minAge = 18;
      const maxAge = 100;

      // Check if it's a valid date
      if (isNaN(birthDate.getTime())) {
        return { isValid: false, error: "INVALID_DATE" };
      }

      // Check if it's in the future
      if (birthDate > today) {
        return { isValid: false, error: "FUTURE_DATE_NOT_ALLOWED" };
      }

      // Check minimum age (18 years old)
      const minDate = new Date();
      minDate.setFullYear(today.getFullYear() - minAge);
      if (birthDate > minDate) {
        return { isValid: false, error: "MINIMUM_AGE_REQUIRED" };
      }

      // Check maximum age (100 years old)
      const maxDate = new Date();
      maxDate.setFullYear(today.getFullYear() - maxAge);
      if (birthDate < maxDate) {
        return { isValid: false, error: "MAXIMUM_AGE_EXCEEDED" };
      }

      return { isValid: true };
    },
  },
  contacto6Nombre: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto6SegundoNombre: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto6ApellidoPaterno: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto6ApellidoMaterno: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto6Telefono: {
    required: false,
    pattern: /^[\d\s]+$/,
    patternDescription: "PHONE_MUST_BE_10_DIGITS",
    customValidation: (value) => {
      if (!value || value.trim() === "") {
        return { isValid: true };
      }

      // Remove all spaces and check if it's exactly 10 digits
      const digitsOnly = value.replace(/\s/g, "");
      if (!/^\d{10}$/.test(digitsOnly)) {
        return { isValid: false, error: "PHONE_MUST_BE_10_DIGITS" };
      }

      return { isValid: true };
    },
  },
  contacto6Celular: {
    required: false,
    pattern: /^[\d\s]+$/,
    patternDescription: "PHONE_MUST_BE_10_DIGITS",
    customValidation: (value) => {
      if (!value || value.trim() === "") {
        return { isValid: true };
      }

      // Remove all spaces and check if it's exactly 10 digits
      const digitsOnly = value.replace(/\s/g, "");
      if (!/^\d{10}$/.test(digitsOnly)) {
        return { isValid: false, error: "PHONE_MUST_BE_10_DIGITS" };
      }

      return { isValid: true };
    },
  },
  contacto6Extension: {
    required: false,
    pattern: /^\d{1,5}$/,
    maxLength: 5,
    patternDescription: "PHONE_EXTENSION_MAX_5_DIGITS",
  },
  contacto6CorreoElectronico: {
    required: false,
    pattern:
      /^(?!.*\.\.)[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z]{2,})+$/,
    maxLength: 100,
    patternDescription: "INVALID_EMAIL",
  },
  contacto6FechaNacimiento: {
    required: false,
    customValidation: (value) => {
      if (!value) {
        return { isValid: true };
      } // Optional field

      const birthDate = new Date(value);
      const today = new Date();
      const minAge = 18;
      const maxAge = 100;

      // Check if it's a valid date
      if (isNaN(birthDate.getTime())) {
        return { isValid: false, error: "INVALID_DATE" };
      }

      // Check if it's in the future
      if (birthDate > today) {
        return { isValid: false, error: "FUTURE_DATE_NOT_ALLOWED" };
      }

      // Check minimum age (18 years old)
      const minDate = new Date();
      minDate.setFullYear(today.getFullYear() - minAge);
      if (birthDate > minDate) {
        return { isValid: false, error: "MINIMUM_AGE_REQUIRED" };
      }

      // Check maximum age (100 years old)
      const maxDate = new Date();
      maxDate.setFullYear(today.getFullYear() - maxAge);
      if (birthDate < maxDate) {
        return { isValid: false, error: "MAXIMUM_AGE_EXCEEDED" };
      }

      return { isValid: true };
    },
  },
  contacto7Nombre: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto7SegundoNombre: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto7ApellidoPaterno: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto7ApellidoMaterno: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto7Telefono: {
    required: false,
    pattern: /^[\d\s]+$/,
    patternDescription: "PHONE_MUST_BE_10_DIGITS",
    customValidation: (value) => {
      if (!value || value.trim() === "") {
        return { isValid: true };
      }

      // Remove all spaces and check if it's exactly 10 digits
      const digitsOnly = value.replace(/\s/g, "");
      if (!/^\d{10}$/.test(digitsOnly)) {
        return { isValid: false, error: "PHONE_MUST_BE_10_DIGITS" };
      }

      return { isValid: true };
    },
  },
  contacto7Celular: {
    required: false,
    pattern: /^[\d\s]+$/,
    patternDescription: "PHONE_MUST_BE_10_DIGITS",
    customValidation: (value) => {
      if (!value || value.trim() === "") {
        return { isValid: true };
      }

      // Remove all spaces and check if it's exactly 10 digits
      const digitsOnly = value.replace(/\s/g, "");
      if (!/^\d{10}$/.test(digitsOnly)) {
        return { isValid: false, error: "PHONE_MUST_BE_10_DIGITS" };
      }

      return { isValid: true };
    },
  },
  contacto7Extension: {
    required: false,
    pattern: /^\d{1,5}$/,
    maxLength: 5,
    patternDescription: "PHONE_EXTENSION_MAX_5_DIGITS",
  },
  contacto7CorreoElectronico: {
    required: false,
    pattern:
      /^(?!.*\.\.)[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z]{2,})+$/,
    maxLength: 100,
    patternDescription: "INVALID_EMAIL",
  },
  contacto7FechaNacimiento: {
    required: false,
    customValidation: (value) => {
      if (!value) {
        return { isValid: true };
      } // Optional field

      const birthDate = new Date(value);
      const today = new Date();
      const minAge = 18;
      const maxAge = 100;

      // Check if it's a valid date
      if (isNaN(birthDate.getTime())) {
        return { isValid: false, error: "INVALID_DATE" };
      }

      // Check if it's in the future
      if (birthDate > today) {
        return { isValid: false, error: "FUTURE_DATE_NOT_ALLOWED" };
      }

      // Check minimum age (18 years old)
      const minDate = new Date();
      minDate.setFullYear(today.getFullYear() - minAge);
      if (birthDate > minDate) {
        return { isValid: false, error: "MINIMUM_AGE_REQUIRED" };
      }

      // Check maximum age (100 years old)
      const maxDate = new Date();
      maxDate.setFullYear(today.getFullYear() - maxAge);
      if (birthDate < maxDate) {
        return { isValid: false, error: "MAXIMUM_AGE_EXCEEDED" };
      }

      return { isValid: true };
    },
  },
  contacto8Nombre: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto8SegundoNombre: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto8ApellidoPaterno: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto8ApellidoMaterno: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto8Telefono: {
    required: false,
    pattern: /^[\d\s]+$/,
    patternDescription: "PHONE_MUST_BE_10_DIGITS",
    customValidation: (value) => {
      if (!value || value.trim() === "") {
        return { isValid: true };
      }

      // Remove all spaces and check if it's exactly 10 digits
      const digitsOnly = value.replace(/\s/g, "");
      if (!/^\d{10}$/.test(digitsOnly)) {
        return { isValid: false, error: "PHONE_MUST_BE_10_DIGITS" };
      }

      return { isValid: true };
    },
  },
  contacto8Celular: {
    required: false,
    pattern: /^[\d\s]+$/,
    patternDescription: "PHONE_MUST_BE_10_DIGITS",
    customValidation: (value) => {
      if (!value || value.trim() === "") {
        return { isValid: true };
      }

      // Remove all spaces and check if it's exactly 10 digits
      const digitsOnly = value.replace(/\s/g, "");
      if (!/^\d{10}$/.test(digitsOnly)) {
        return { isValid: false, error: "PHONE_MUST_BE_10_DIGITS" };
      }

      return { isValid: true };
    },
  },
  contacto8Extension: {
    required: false,
    pattern: /^\d{1,5}$/,
    maxLength: 5,
    patternDescription: "PHONE_EXTENSION_MAX_5_DIGITS",
  },
  contacto8CorreoElectronico: {
    required: false,
    pattern:
      /^(?!.*\.\.)[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z]{2,})+$/,
    maxLength: 100,
    patternDescription: "INVALID_EMAIL",
  },
  contacto8FechaNacimiento: {
    required: false,
    customValidation: (value) => {
      if (!value) {
        return { isValid: true };
      } // Optional field

      const birthDate = new Date(value);
      const today = new Date();
      const minAge = 18;
      const maxAge = 100;

      // Check if it's a valid date
      if (isNaN(birthDate.getTime())) {
        return { isValid: false, error: "INVALID_DATE" };
      }

      // Check if it's in the future
      if (birthDate > today) {
        return { isValid: false, error: "FUTURE_DATE_NOT_ALLOWED" };
      }

      // Check minimum age (18 years old)
      const minDate = new Date();
      minDate.setFullYear(today.getFullYear() - minAge);
      if (birthDate > minDate) {
        return { isValid: false, error: "MINIMUM_AGE_REQUIRED" };
      }

      // Check maximum age (100 years old)
      const maxDate = new Date();
      maxDate.setFullYear(today.getFullYear() - maxAge);
      if (birthDate < maxDate) {
        return { isValid: false, error: "MAXIMUM_AGE_EXCEEDED" };
      }

      return { isValid: true };
    },
  },
  contacto9Nombre: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto9SegundoNombre: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto9ApellidoPaterno: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto9ApellidoMaterno: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto9Telefono: {
    required: false,
    pattern: /^[\d\s]+$/,
    patternDescription: "PHONE_MUST_BE_10_DIGITS",
    customValidation: (value) => {
      if (!value || value.trim() === "") {
        return { isValid: true };
      }

      // Remove all spaces and check if it's exactly 10 digits
      const digitsOnly = value.replace(/\s/g, "");
      if (!/^\d{10}$/.test(digitsOnly)) {
        return { isValid: false, error: "PHONE_MUST_BE_10_DIGITS" };
      }

      return { isValid: true };
    },
  },
  contacto9Celular: {
    required: false,
    pattern: /^[\d\s]+$/,
    patternDescription: "PHONE_MUST_BE_10_DIGITS",
    customValidation: (value) => {
      if (!value || value.trim() === "") {
        return { isValid: true };
      }

      // Remove all spaces and check if it's exactly 10 digits
      const digitsOnly = value.replace(/\s/g, "");
      if (!/^\d{10}$/.test(digitsOnly)) {
        return { isValid: false, error: "PHONE_MUST_BE_10_DIGITS" };
      }

      return { isValid: true };
    },
  },
  contacto9Extension: {
    required: false,
    pattern: /^\d{1,5}$/,
    maxLength: 5,
    patternDescription: "PHONE_EXTENSION_MAX_5_DIGITS",
  },
  contacto9CorreoElectronico: {
    required: false,
    pattern:
      /^(?!.*\.\.)[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z]{2,})+$/,
    maxLength: 100,
    patternDescription: "INVALID_EMAIL",
  },
  contacto9FechaNacimiento: {
    required: false,
    customValidation: (value) => {
      if (!value) {
        return { isValid: true };
      } // Optional field

      const birthDate = new Date(value);
      const today = new Date();
      const minAge = 18;
      const maxAge = 100;

      // Check if it's a valid date
      if (isNaN(birthDate.getTime())) {
        return { isValid: false, error: "INVALID_DATE" };
      }

      // Check if it's in the future
      if (birthDate > today) {
        return { isValid: false, error: "FUTURE_DATE_NOT_ALLOWED" };
      }

      // Check minimum age (18 years old)
      const minDate = new Date();
      minDate.setFullYear(today.getFullYear() - minAge);
      if (birthDate > minDate) {
        return { isValid: false, error: "MINIMUM_AGE_REQUIRED" };
      }

      // Check maximum age (100 years old)
      const maxDate = new Date();
      maxDate.setFullYear(today.getFullYear() - maxAge);
      if (birthDate < maxDate) {
        return { isValid: false, error: "MAXIMUM_AGE_EXCEEDED" };
      }

      return { isValid: true };
    },
  },
  contacto10Nombre: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto10SegundoNombre: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto10ApellidoPaterno: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto10ApellidoMaterno: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  contacto10Telefono: {
    required: false,
    pattern: /^[\d\s]+$/,
    patternDescription: "PHONE_MUST_BE_10_DIGITS",
    customValidation: (value) => {
      if (!value || value.trim() === "") {
        return { isValid: true };
      }

      // Remove all spaces and check if it's exactly 10 digits
      const digitsOnly = value.replace(/\s/g, "");
      if (!/^\d{10}$/.test(digitsOnly)) {
        return { isValid: false, error: "PHONE_MUST_BE_10_DIGITS" };
      }

      return { isValid: true };
    },
  },
  contacto10Celular: {
    required: false,
    pattern: /^[\d\s]+$/,
    patternDescription: "PHONE_MUST_BE_10_DIGITS",
    customValidation: (value) => {
      if (!value || value.trim() === "") {
        return { isValid: true };
      }

      // Remove all spaces and check if it's exactly 10 digits
      const digitsOnly = value.replace(/\s/g, "");
      if (!/^\d{10}$/.test(digitsOnly)) {
        return { isValid: false, error: "PHONE_MUST_BE_10_DIGITS" };
      }

      return { isValid: true };
    },
  },
  contacto10Extension: {
    required: false,
    pattern: /^\d{1,5}$/,
    maxLength: 5,
    patternDescription: "PHONE_EXTENSION_MAX_5_DIGITS",
  },
  contacto10CorreoElectronico: {
    required: false,
    pattern:
      /^(?!.*\.\.)[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z]{2,})+$/,
    maxLength: 100,
    patternDescription: "INVALID_EMAIL",
  },
  contacto10FechaNacimiento: {
    required: false,
    customValidation: (value) => {
      if (!value) {
        return { isValid: true };
      } // Optional field

      const birthDate = new Date(value);
      const today = new Date();
      const minAge = 18;
      const maxAge = 100;

      // Check if it's a valid date
      if (isNaN(birthDate.getTime())) {
        return { isValid: false, error: "INVALID_DATE" };
      }

      // Check if it's in the future
      if (birthDate > today) {
        return { isValid: false, error: "FUTURE_DATE_NOT_ALLOWED" };
      }

      // Check minimum age (18 years old)
      const minDate = new Date();
      minDate.setFullYear(today.getFullYear() - minAge);
      if (birthDate > minDate) {
        return { isValid: false, error: "MINIMUM_AGE_REQUIRED" };
      }

      // Check maximum age (100 years old)
      const maxDate = new Date();
      maxDate.setFullYear(today.getFullYear() - maxAge);
      if (birthDate < maxDate) {
        return { isValid: false, error: "MAXIMUM_AGE_EXCEEDED" };
      }

      return { isValid: true };
    },
  },
  // Event registration modal validation rules
  nombre: {
    required: false,
    pattern: /^[a-zA-ZÀ-ÿ\s]+$/,
    maxLength: 100,
    patternDescription: "LETTERS_ONLY",
  },
  telefono: {
    required: false,
    pattern: /^[\d\s]+$/,
    patternDescription: "PHONE_MUST_BE_10_DIGITS",
    customValidation: (value) => {
      if (!value || value.trim() === "") {
        return { isValid: true };
      }

      // Remove all spaces and check if it's exactly 10 digits
      const digitsOnly = value.replace(/\s/g, "");
      if (!/^\d{10}$/.test(digitsOnly)) {
        return { isValid: false, error: "PHONE_MUST_BE_10_DIGITS" };
      }

      return { isValid: true };
    },
  },
  correoElectronico: {
    required: false,
    pattern:
      /^(?!.*\.\.)[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z]{2,})+$/,
    maxLength: 100,
    patternDescription: "INVALID_EMAIL",
  },
  numeroTotalTrabajadores: {
    required: false,
    pattern: /^\d+$/,
    patternDescription: "NUMBERS_ONLY",
    customValidation: (value) => {
      if (!value || value.trim() === "") {
        return { isValid: true };
      }

      // Only allow positive integers
      if (!/^\d+$/.test(value.trim())) {
        return { isValid: false, error: "NUMBERS_ONLY" };
      }

      const numValue = parseInt(value.trim(), 10);
      if (isNaN(numValue) || numValue < 0) {
        return { isValid: false, error: "NUMBERS_ONLY" };
      }

      return { isValid: true };
    },
  },
};

/** Coerces form values to a string before trim/pattern checks (API may return numbers). */
const toValidationString = (value) => (value == null ? "" : String(value));

// Main validation function
export const validateField = (value, fieldName, rules = null) => {
  const fieldRules = rules || VALIDATION_RULES[fieldName];

  if (!fieldRules) {
    return { isValid: true, error: null };
  }

  const str = toValidationString(value);

  // Check if field is required
  if (fieldRules.required && (!str || str.trim() === "")) {
    return {
      isValid: false,
      error: "FIELD_REQUIRED",
    };
  }

  // Skip other validations if field is empty and not required
  if (!str || str.trim() === "") {
    return { isValid: true, error: null };
  }

  const trimmedValue = str.trim();

  // Check max length
  if (fieldRules.maxLength && trimmedValue.length > fieldRules.maxLength) {
    return {
      isValid: false,
      error: "FIELD_TOO_LONG",
      maxLength: fieldRules.maxLength,
      currentLength: trimmedValue.length,
    };
  }

  // Check min length
  if (fieldRules.minLength && trimmedValue.length < fieldRules.minLength) {
    return {
      isValid: false,
      error: "FIELD_TOO_SHORT",
      minLength: fieldRules.minLength,
      currentLength: trimmedValue.length,
    };
  }

  // Check pattern
  if (fieldRules.pattern && !fieldRules.pattern.test(trimmedValue)) {
    return {
      isValid: false,
      error: "FIELD_INVALID_PATTERN",
      patternDescription: fieldRules.patternDescription,
    };
  }

  // Handle custom validation functions
  if (fieldRules.customValidation) {
    return fieldRules.customValidation(str);
  }

  return { isValid: true, error: null };
};

// Get validation state for UI
export const getValidationState = (value, fieldName, rules = null) => {
  const str = toValidationString(value);
  if (!str || str.trim() === "") {
    return "neutral";
  }

  const validation = validateField(str, fieldName, rules);
  return validation.isValid ? "valid" : "error";
};
